import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Trophy, Plus } from "lucide-react";

export default function Tournaments() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [beats, setBeats] = useState([]);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [size, setSize] = useState(4);
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tournaments");
    setList(data);
  };
  const loadBeats = async () => {
    const { data } = await api.get("/beats?sort=top&limit=50");
    setBeats(data);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (show) loadBeats(); }, [show]);

  const toggleBeat = (id) => {
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : s.length < size ? [...s, id] : s));
  };

  const create = async (e) => {
    e.preventDefault();
    if (selected.length !== size) {
      toast.error(`Pick exactly ${size} beats`);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/tournaments", { name, description: desc, beat_ids: selected });
      toast.success("Tournament created");
      setShow(false);
      setName(""); setDesc(""); setSelected([]);
      load();
      window.location.href = `/tournaments/${data.id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="tournaments-page" className="px-6 md:px-12 py-12 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="font-mono-acc text-xs text-secondary">// BRACKETS</div>
          <h1 className="font-display text-5xl md:text-6xl mt-3">Tournaments.</h1>
        </div>
        {user && typeof user === "object" && (
          <button
            data-testid="create-tournament-btn"
            onClick={() => setShow(true)}
            className="font-mono-acc text-xs px-5 py-3 border border-secondary text-secondary hover:bg-secondary hover:text-background inline-flex items-center gap-2"
          >
            <Plus size={14} /> New Tournament
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div data-testid="tournaments-empty" className="border border-border p-12 text-center">
          <Trophy className="mx-auto text-secondary mb-4" />
          <p className="font-display text-3xl">No brackets yet.</p>
          <p className="text-muted-foreground mt-2">Spin up the first one with 4 or 8 beats.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {list.map((t) => (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              data-testid={`tournament-${t.id}`}
              className="border border-border bg-card p-6 hover:border-secondary group"
            >
              <div className="flex items-center justify-between">
                <span className={`font-mono-acc text-[10px] ${t.status === "complete" ? "text-secondary" : "text-primary"}`}>
                  {t.status === "complete" ? "■ COMPLETE" : "● LIVE"}
                </span>
                <span className="font-mono-acc text-[10px] text-muted-foreground">{t.rounds?.[0]?.length * 2 || 0} beats</span>
              </div>
              <h3 className="font-display text-3xl mt-4 group-hover:text-secondary">{t.name}</h3>
              <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{t.description || "—"}</p>
            </Link>
          ))}
        </div>
      )}

      {show && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <form
            onSubmit={create}
            className="bg-card border border-border max-w-3xl w-full max-h-[85vh] overflow-auto p-6"
            data-testid="create-tournament-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-3xl">New Tournament</h2>
              <button type="button" onClick={() => setShow(false)} className="font-mono-acc text-xs border border-border px-3 py-1 hover:border-primary">
                Close
              </button>
            </div>
            <div className="space-y-3">
              <input
                data-testid="t-name"
                placeholder="Tournament name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-input border border-border px-3 py-3 outline-none focus:border-primary"
              />
              <textarea
                data-testid="t-desc"
                placeholder="Description (optional)"
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                rows={2}
                className="w-full bg-input border border-border px-3 py-3 outline-none focus:border-primary"
              />
              <div className="flex gap-2">
                {[4, 8].map((s) => (
                  <button
                    type="button"
                    key={s}
                    data-testid={`t-size-${s}`}
                    onClick={() => { setSize(s); setSelected([]); }}
                    className={`font-mono-acc text-xs px-4 py-2 border ${
                      size === s ? "border-primary bg-primary text-white" : "border-border"
                    }`}
                  >
                    {s} beats
                  </button>
                ))}
              </div>
              <div className="font-mono-acc text-[10px] text-muted-foreground">
                Select {size} beats ({selected.length}/{size})
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-72 overflow-auto border border-border p-2">
                {beats.map((b) => {
                  const on = selected.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      data-testid={`t-pick-${b.id}`}
                      onClick={() => toggleBeat(b.id)}
                      className={`text-left p-2 border ${on ? "border-primary bg-primary/10" : "border-border hover:border-foreground"}`}
                    >
                      <p className="font-display text-sm truncate">{b.title}</p>
                      <p className="font-mono-acc text-[9px] text-muted-foreground">@{b.username}</p>
                    </button>
                  );
                })}
              </div>
              <button
                data-testid="t-create-submit"
                disabled={submitting || selected.length !== size}
                className="w-full border border-primary bg-primary text-white py-3 font-mono-acc text-xs hover:bg-white hover:text-primary disabled:opacity-50"
              >
                {submitting ? "Creating..." : "Create Tournament"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
